
(function () {

    /**
     *
     * @param {() => boolean} testFcn - When this function returns a truthy value, we are done.
     * @param {function} cb - The callback function we call when fcn evaluates to a truthy value or we timeout.
     * @param {number} [interval] - The minimum number of milliseconds to wait in between checks.
     * @param {number} [limit] - The maximum number of failures we allow before throwing an error.
     * @return {*}
     */
    function waitUntilTrue(
        testFcn, // When this function returns a truthy value, we are done.
        cb, // The callback function we call when fcn evaluates to a truthy value or we timeout.
        interval = 200, // The minimum number of milliseconds to wait in between checks.
        limit = 50 // The maximum number of failures we allow before throwing an error.
    ) {
        if (testFcn()) {
            return cb();
        }
        limit -= 1;
        if (limit === 0) {
            return cb(true);
        }
        setTimeout(waitUntilTrue, interval, testFcn, cb, interval, limit);
    }

//
// Class Utilities
//

    /**
     * @param initialObj {object}
     * @return {Set<[object,(string|Symbol)]>}
     */
    const getAllNonBuiltInProperties = function (initialObj) {
        const properties = new Set();
        (function getNonBuiltInProperties(obj) {
            if (obj) {
                Reflect.ownKeys(obj).forEach(key => {
                    properties.add([obj, key]);
                });
            }
            const proto = Reflect.getPrototypeOf(obj);
            if (proto && proto !== Object.prototype) {
                getNonBuiltInProperties(proto);
            }
        })(initialObj);
        return properties;
    };

    /**
     * A convenience function which takes a class's 'this' value and binds all the class's methods to it.
     *
     * @param {object} obj - Typically the 'this' value from the class.
     * @param {Array<RegExp | string>} [include]  - Methods to bind to obj.
     * @param {Array<RegExp | string>} [exclude]  - Methods to NOT bind to obj.
     * @return {object}
     */
    const bindThisToAllMethods = function (obj, include, exclude) {
        const filter = (key) => {
            if (typeof key === "symbol") {
                return true;
            }
            const match = (pattern) => (
                typeof pattern === "string"
                ? key === pattern
                : pattern.test(key)
            );
            return (
                include ? include.some(match)
                : exclude ? !exclude.some(match)
                : true
            );
        };

        for (const [object, key] of getAllNonBuiltInProperties(obj.constructor.prototype)) {
            if (key === "constructor" || !filter(key)) {
                continue;
            }
            const descriptor = Reflect.getOwnPropertyDescriptor(object, key);
            if (descriptor && typeof descriptor.value === "function") {
                obj[key] = obj[key].bind(obj);
            }
        }

        return obj;
    };


//
// DOM
//

    /**
     * Calls 'callback' once the page has been repainted.
     *
     * @param {function} callback
     */
    const afterNextFrame = function (callback) {
        window.requestAnimationFrame(() => {
            setTimeout(callback, 0);
        });
    };

    /**
     *
     * @param {Node} node
     * @return {string}
     */
    const convertNodeToString = function (node) {
        const xmlSerializer = new XMLSerializer();
        return xmlSerializer.serializeToString(node);
    };

    /**
     *
     * @param {HTMLElement} oldElement
     * @param {HTMLElement} newElement
     */
    const replaceElement = function (oldElement, newElement) {
        oldElement.parentElement.insertBefore(newElement, oldElement);
        oldElement.parentElement.removeChild(oldElement);
    };

    /**
     *
     * @param {string} str
     * @return {DocumentFragment}
     */
    const stringToDocFrag = function (str) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(str, "text/html");
        const bodyChildren = Array.from(doc.body.children);
        const docFrag = document.createDocumentFragment();
        bodyChildren.forEach(child => {
            docFrag.appendChild(child);
        });
        return docFrag;
    };

    /**
     * @return {HTMLSpanElement}
     */
    const buildSpinnerIcon = function () {
        /*
            Returns a spinner icon wrapped in a hidden [data-loader] span.
            Required by setLoadingState.
         */
        const ns = "http://www.w3.org/2000/svg";

        const svg = document.createElementNS(ns, "svg");
        svg.setAttribute("aria-hidden", "true");
        svg.setAttribute("focusable", "false");
        svg.setAttribute("role", "presentation");
        svg.setAttribute("class", "icon spinner-icon");
        svg.setAttribute("viewBox", "0 0 20 20");

        const path = document.createElementNS(ns, "path");
        path.setAttribute("d", "M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z");
        path.setAttribute("fill", "currentColor");

        svg.appendChild(path);

        const wrapper = document.createElement("span");
        wrapper.setAttribute("data-loader", "true");
        wrapper.className = "is-hidden";
        wrapper.appendChild(svg);

        return wrapper;
    };

    /**
     *
     * @param {HTMLElement} container
     * @param {boolean} loading
     */
    const setLoadingState = function (container, loading) {
        /*
            Takes a container element and starts the loading animation if 'loading' is true,
            otherwise it stops the loading animation. If there is no load svg inside the button,
            we build one.

            This function requires the styles found in plug-utils/scss/loading-spinner.scss.
         */

        // Since the spinner is absolutely positioned inside 'container',
        // 'container' cannot have position=static when loading=true.
        if (loading) {
            if (getComputedStyle(container).getPropertyValue("position") === "static") {
                container.dataset.positionWasStatic = "true";
                container.style.position = "relative";
            }
        } else {
            if (container.dataset.positionWasStatic === "true") {
                delete container.dataset.positionWasStatic
                container.style.position = "";
            }
        }

        let loader = container.querySelector("[data-loader]");
        let content = container.querySelector("[data-content]");

        if (!loader) {
            loader = buildSpinnerIcon();

            if (!content) {
                content = document.createElement("span");
                content.setAttribute("data-content", "true");

                let k = 0;
                while (k < container.childNodes.length) {
                    let childNodeK = container.childNodes[k];
                    if (childNodeK.nodeType === 1 || childNodeK.nodeType === 3) { // If it is an element or text node.
                        content.appendChild(childNodeK);
                    }
                    k += 1;
                }

                container.appendChild(content);
            }

            container.appendChild(loader);
        }

        if (!content) { // Loader is present but content is not.
            return;
        }

        if (loading) {
            if ("disabled" in container) {
                container.disabled = true;
            }
            content.classList.add("is-see-through");
            loader.classList.remove("is-hidden");
        } else {
            if ("disabled" in container) {
                container.disabled = false;
            }
            loader.classList.add("is-hidden");
            content.classList.remove("is-see-through");
        }
    };

    /**
     * @param {string} name
     * @return {string|undefined}
     */
    const getCookieValue = function (name) {
        const cookieString = document.cookie.split("; ").find(cookieString => {
            return cookieString.split("=")[0] === name;
        });
        if (cookieString) {
            return cookieString.split("=")[1];
        }
    };

    /**
     * @param {string} name
     * @param {string} value
     * @param {{
     *   ["max-age"]: number,
     *   [path]: string,
     *   [samesite]: "lax" | "strict" | "none",
     *   [secure]: boolean
     * }} [options]
     */
    const setCookie = function (name, value, options) {
        const str = Object.keys(options).reduce(
            (cookieString, optionKey) => {
                if (optionKey !== "secure") {
                    return `${cookieString}; ${optionKey}=${options[optionKey]}`;
                } else if (options[optionKey]) {
                    return `${cookieString}; secure`;
                } else {
                    return cookieString;
                }
            },
            `${name}=${value}`
        );
        document.cookie = str;
    };

//
// Shopify Product API
//

    /**
     * Retrieve a product from the Shopify Product API.
     *
     * @param {string} handle
     * @return {Promise<any>}
     */
    const getProduct = function (handle) {
        return fetch(`/products/${handle}.js`, {
            headers: {
                "Content-Type": "application/json"
            }
        }).then(data => data.json());
    };


//
// Shopify Section Rendering API Utilities
//

    /**
     * Fetch a section using Shopify's section rendering API.
     *
     * @param sectionId {string} - The ID of the section to fetch, does not include 'shopify-section-'.
     * @param path {string} - The path to make the request to.
     * @param queryString {string} - E.g. ?variant_id=123456678&prop=value
     * @return {Promise<Element>}
     */
    const fetchSection = function (sectionId, path=location.pathname, queryString=location.search) {
        const url = (
            queryString
                ? path + queryString + "&section_id=" + sectionId
                : path + "?section_id=" + sectionId
        );
        return fetch(url)
            .then(res => res.text())
            .then(newSectionText => {
                const domparser = new DOMParser();
                const parsedDoc = domparser.parseFromString(newSectionText, "text/html");
                return parsedDoc.body.children[0];
            });
    };

    /**
     * Using the section rendering API, fetch a section and replace the matching section on the current page with it.
     *
     * @param sectionId {string} - The ID of the section to fetch, does not include 'shopify-section-'.
     * @param path {string} - The path to make the request to.
     * @param queryString {string} - E.g. ?variant_id=123456678&prop=value
     * @return {Promise<Element>}
     */
    const refreshSection = function (sectionId, path=location.pathname, queryString=location.search) {
        return fetchSection(sectionId, path, queryString)
            .then(newSection => {
                const oldSection = document.getElementById("shopify-section-" + sectionId);
                if (!oldSection) {
                    throw new Error("Cannot find section to replace.");
                }

                const sectionParent = oldSection.parentElement;
                if (!sectionParent) {
                    throw new Error("Cannot call refresh section on the document root.");
                }
                sectionParent.insertBefore(newSection, oldSection);
                sectionParent.removeChild(oldSection);
                return newSection;
            });
    };


//
// Shopify Money
//

    /**
     *
     * @param {number} price
     * @param {boolean} [useCents]
     * @param {boolean} [removeTrailingZeros]
     * @return {string}
     */
    const formatMoney = function (price, useCents=true, removeTrailingZeros=false) {
        const format = window.PLUG.liquid.moneyFormat;
        let money;
        let formattedAmount; // The numerical portion of the price.
        formattedAmount = (useCents ? price/100 : price).toFixed(2);
        if (removeTrailingZeros) {
            formattedAmount = formattedAmount.replace(".00", "");
        }

        if (format.includes("{{amount}}")) {
            money = format.replace("{{amount}}", formattedAmount);
        } else if (format.includes("{{amount_with_comma_separator}}")) {
            money = format.replace("{{amount_with_comma_separator}}", formattedAmount);
            money = money.replace(".", ",");
        } else {
            money = format.replace("{{amount_no_decimals}}", formattedAmount);
        }

        return money;
    }

//
// Dawn
//

    /**
     *
     * @param {Product} product
     * @param {Variant} variant
     * @param {boolean} [showBadges]
     * @return {string}
     */
    const buildPriceSnippet = function (product, variant, showBadges=false) {
        const priceCents = variant.price;
        const compareAtPriceCents = variant.compare_at_price;

        const priceClasses = ["price", "main-product-price"];
        let hasNoCompare = false;
        if (!variant.available) {
            priceClasses.push("price--sold-out");
        }
        if (compareAtPriceCents && compareAtPriceCents > priceCents) {
            priceClasses.push("price--on-sale");
        }
        if (product && (!product.price_varies && product.compare_at_price_varies)) {
            priceClasses.push("price--no-compare");
            hasNoCompare = true;
        }
        if (showBadges) {
            priceClasses.push("price--show-badges");
        }

        return (
            `<div class="${priceClasses.join(" ")}">
                <div class="price__container">
                    <div class="price__regular">
                        <span class="visually-hidden visually-hidden--inline">${window.plugPriceStrings.regularPrice}</span>
                        <span class="price-item price-item--regular">${PLUG.utils.formatMoney(priceCents)}</span>
                    </div>
                
                    <div class="price__sale">
                        ${!hasNoCompare
                            ? (
                                `<span class="visually-hidden visually-hidden--inline">${window.plugPriceStrings.regularPrice}</span>
                                <span>
                                    <s class="price-item price-item--regular">${PLUG.utils.formatMoney(compareAtPriceCents)}</s>
                                </span>`
                            )
                            : ""
                        }
                        <span class="visually-hidden visually-hidden--inline">${window.plugPriceStrings.salePrice}</span>
                        <span class="price-item price-item--sale price-item--last">${PLUG.utils.formatMoney(priceCents)}</span>
                    </div>
                </div>
                ${showBadges
                    ? (
                        `<span class="badge price__badge-sale color-${window.plugThemeSettings.saleBadgeColorScheme}">${window.plugPriceStrings.onSale}</span>
                        <span class="badge price__badge-sold-out color-${window.plugThemeSettings.soldOutBadgeColorScheme}">${window.plugPriceStrings.soldOut}</span>`
                    )
                    : ""
                }
            </div>`
        );
    };

//
// Store Specific
//

    const stylePrefixes = ["Men's", "Women's", "Kid's"];
    const stylePrefixesLowerCase = stylePrefixes.map(p => p.toLowerCase());

    /**
     *
     * @param {string} style - e.g. "Unisex Hoodie"
     * @param {string} department - e.g. "Men's Apparel"
     * @return {string} - e.g. "Hoodie"
     */
    const getStyleDisplayName = function (style, department) {
        let words = style.split(" ");
        words = words.filter(word => !stylePrefixesLowerCase.includes(word.toLowerCase()));
        if (department.toLowerCase().startsWith("men's")) {
            words = words.filter(word => word.toLowerCase() !== "unisex");
        }
        return words.join(" ");
    };

    /**
     * @param {string | number} printfulProductId
     * @return {Promise<{countryCode: string, html: string, is_success: boolean}>}
     */
    const getPrintfulEdt = function (printfulProductId) {
        return new Promise((resolve, reject) => {
            if (!printfulProductId || !window.Shopify || !window.Shopify.locale || !window.Shopify.shop) {
                return reject("Missing 'printfulProductId' or one or more required global variables.");
            }

            // Use cached value if one exists from within the last 4 hours.
            const storageProductEdtKey = `${Shopify.locale}_${printfulProductId}`;
            const storageProductEdtCachedTimeKey = `${Shopify.locale}_edtCached_${printfulProductId}`;
            let cachedEdt = localStorage.getItem(storageProductEdtKey);
            if (cachedEdt) {
                cachedEdt = JSON.parse(cachedEdt);
                if (!cachedEdt.result.is_success) {
                    return resolve(cachedEdt.result);
                }
                const cachedEdtTime = localStorage.getItem(storageProductEdtCachedTimeKey);
                if (cachedEdtTime && Date.now() - Number(cachedEdtTime) < 144e5) {
                    return resolve(cachedEdt.result);
                }
            }

            fetch(`https://api.printful.com/estimate-delivery-time?productId=${printfulProductId}&locale=${Shopify.locale}&storeIdentity=${Shopify.shop}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded"
                }
            })
                .then(data => data.json())
                .then(data => {
                    if (data.result) {
                        // Cache the result.
                        if (data.result.is_success || "internal_server_error" !== data.result.reason_code) {
                            try {
                                localStorage.setItem(storageProductEdtKey, JSON.stringify(data));
                                localStorage.setItem(storageProductEdtCachedTimeKey, Date.now().toString());
                            } catch(err) {
                                localStorage.removeItem(storageProductEdtKey);
                                localStorage.removeItem(storageProductEdtCachedTimeKey);
                                console.error(err);
                            }
                        }
                        resolve(data.result)
                    } else {
                        reject("Unexpected format for Printful API response.");
                    }
                })
                .catch(err => reject(err));
        });
    };


    window.PLUG = window.PLUG || {};
    window.PLUG.utils = Object.freeze({
        waitUntilTrue,

        bindThisToAllMethods,

        afterNextFrame,
        convertNodeToString,
        replaceElement,
        stringToDocFrag,
        setLoadingState,
        getCookieValue,
        setCookie,

        getProduct,

        fetchSection,
        refreshSection,

        formatMoney,

        buildPriceSnippet,

        getStyleDisplayName,
        getPrintfulEdt,
    });
})();
